import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ProjectSummary, ReportSummary } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Atlas } from '@/components/home/atlas'
import { Closing } from '@/components/home/closing'
import { Evidence } from '@/components/home/evidence'
import { HomeExperience } from '@/components/home/home-experience'
import { HomeReports } from '@/components/home/home-reports'
import { Manifesto } from '@/components/home/manifesto'
import { PlatformPinned } from '@/components/home/platform-pinned'
import { SelectedWork } from '@/components/home/selected-work'
import { getProjects, getReports } from '@/lib/content'
import { shouldRethrow } from '@/lib/db'

interface HomeProps {
    projects: Array<ProjectSummary>
    reports: Array<ReportSummary>
}

const Home: FC<HomeProps> = ({ projects = [], reports = [] }): ReactNode => {
    return (
        <Layout variant='home'>
            <Seo figure='Fig. 00 / Norrvik / Live model' shape='library' />

            <HomeExperience />
            <Manifesto />
            <PlatformPinned />
            <Evidence projects={projects} />
            <SelectedWork projects={projects} />
            <Atlas projects={projects} />
            <HomeReports reports={reports} />
            <Closing />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const [projects, reports] = await Promise.all([getProjects(), getReports()])

        return { props: { projects, reports }, revalidate: 60 }
    }
    catch (error) {
        console.error('Home content could not be loaded:', error instanceof Error ? error.message : error)

        if (shouldRethrow()) throw error

        return { props: { projects: [], reports: [] }, revalidate: 30 }
    }
}

export default Home
